/**
 * Spinner Component
 *
 * A loading indicator component with multiple styles and sizes.
 * Based on the design system's Loading indicator component.
 *
 * @example
 * // Basic usage
 * <Spinner />
 *
 * @example
 * // With different variants
 * <Spinner variant="line-simple" />
 * <Spinner variant="line-spinner" />
 * <Spinner variant="dot-circle" />
 *
 * @example
 * // With different sizes
 * <Spinner size="xs" />
 * <Spinner size="sm" />
 * <Spinner size="md" />
 * <Spinner size="lg" />
 * <Spinner size="xl" />
 *
 * @example
 * // With custom text
 * <Spinner text="Please wait..." />
 *
 * @example
 * // Without text (icon only)
 * <Spinner showText={false} />
 *
 * @example
 * // With custom color
 * <Spinner colorClass="text-icon-success" />
 * <Spinner colorClass="text-icon-error" />
 * <Spinner colorClass="text-icon-warning" />
 *
 * @example
 * // With custom animation duration
 * <Spinner duration={0.8} />  // faster
 * <Spinner duration={1.5} />  // slower
 */
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'src/utils/utils';
import { Icon } from './icon';
import { Typography } from './typography';

const spinnerContainerVariants = cva(
  'tw:inline-flex tw:flex-col tw:items-center tw:justify-center',
  {
    variants: {
      size: {
        xs: 'tw:gap-s',
        sm: 'tw:gap-m',
        md: 'tw:gap-l',
        lg: 'tw:gap-l',
        xl: 'tw:gap-xl'
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
);

/**
 * Size mappings from Figma design system
 *
 * These values correspond to design tokens:
 * - xs: 16px (--Size-H-m), stroke 1.5px (--Stroke-1p5)
 * - sm: 32px (--Size-H-2xl), stroke 4px (--Stroke-4)
 * - md: 48px (--Spacing-gn-48px), stroke 5px (--Stroke-5)
 * - lg: 56px, stroke 5px (--Stroke-5)
 * - xl: 64px (--Spacing-gn-64px), stroke 8px (--Spacing-gn-8px)
 *
 * Note: SVG attributes require numeric values, so we use the resolved
 * pixel values here. The CSS variable names are documented for reference.
 */
const sizeConfig = {
  xs: {
    // --Size-H-m (16px), --Stroke-1p5 (1.5px)
    dimensionVar: 'var(--Size-H-m)',
    dimension: 16,
    strokeWidthVar: 'var(--Stroke-1p5)',
    strokeWidth: 1.5,
    typography: 'h6' as const,
    iconClass: 'tw:h-xs tw:w-xs'
  },
  sm: {
    // --Size-H-2xl (32px), --Stroke-4 (4px)
    dimensionVar: 'var(--Size-H-2xl)',
    dimension: 32,
    strokeWidthVar: 'var(--Stroke-4)',
    strokeWidth: 4,
    typography: 'h5' as const,
    iconClass: 'tw:h-l tw:w-l'
  },
  md: {
    // --Spacing-gn-48px (48px), --Stroke-5 (5px)
    dimensionVar: 'var(--Spacing-gn-48px)',
    dimension: 48,
    strokeWidthVar: 'var(--Stroke-5)',
    strokeWidth: 5,
    typography: 'h4' as const,
    iconClass: 'tw:h-xl tw:w-xl'
  },
  lg: {
    // 56px (no exact token), --Stroke-5 (5px)
    dimensionVar: '56px',
    dimension: 56,
    strokeWidthVar: 'var(--Stroke-5)',
    strokeWidth: 5,
    typography: 'h3' as const,
    iconClass: 'tw:h-2xl tw:w-2xl'
  },
  xl: {
    // --Spacing-gn-64px (64px), --Spacing-gn-8px (8px)
    dimensionVar: 'var(--Spacing-gn-64px)',
    dimension: 64,
    strokeWidthVar: 'var(--Spacing-gn-8px)',
    strokeWidth: 8,
    typography: 'h2' as const,
    iconClass: 'tw:h-2xl tw:w-2xl'
  }
};

export interface SpinnerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof spinnerContainerVariants> {
  /**
   * The visual variant of the spinner.
   * - line-simple: Simple rotating arc with a track circle and a progress arc
   * - line-spinner: Simple rotating arc with a progress arc and without a track circle
   * - dot-circle: Dotted circle with gradient mask
   *
   * @default 'line-simple'
   */
  variant?: 'line-simple' | 'line-spinner' | 'dot-circle';

  /**
   * The size of the spinner.
   * Maps to specific dimensions and stroke widths.
   *
   * @default 'md'
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Loading text to display below the spinner.
   *
   * @default 'Loading...'
   */
  text?: string;

  /**
   * Whether to show the loading text.
   *
   * @default true
   */
  showText?: boolean;

  /**
   * Color class for the progress circle/dots.
   * Use Tailwind text color classes (e.g., 'text-icon-brand', 'text-icon-error').
   * The SVG stroke will inherit from currentColor.
   *
   * @example
   * colorClass="text-icon-brand"
   * colorClass="text-icon-success"
   * colorClass="text-icon-error"
   *
   * @default 'text-icon-brand' (uses --Color-Primary-500)
   */
  colorClass?: string;

  /**
   * Animation duration in seconds.
   *
   * @example
   * duration={0.8}  // faster
   * duration={1.5}  // slower
   *
   * @default 1 (for line variants), 1.2 (for dot-circle)
   */
  duration?: number;

  /**
   * Optional glyph rendered at the CENTRE of the ring, sized per `size`.
   *
   * Orthogonal to `variant`: any spinner style can carry one. It used to be a
   * bespoke `image-loader` variant with hard-coded 36px geometry, which meant
   * `size` silently did nothing for the one case that wanted an icon.
   */
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;

  /**
   * Colour of the STATIC track circle behind the moving arc — `line-simple`
   * only, since it is the one variant that draws a track.
   *
   * Separate from `colorClass` on purpose: the track is the UNFILLED part of the
   * ring, so if it took the arc's colour the arc would be invisible. The two
   * always contrast.
   *
   * A Tailwind `stroke-*` class, like every other colour prop on this component.
   *
   * @example
   * trackColorClass="tw:stroke-border-primary"
   *
   * @default 'tw:stroke-tertiary'
   */
  trackColorClass?: string;

  /**
   * Override the ring thickness in px, for a design that specifies a stroke the
   * `size` scale does not carry — e.g. a 1.5px hairline at a size whose default
   * is 4px. Everything else (dimension, icon size, text) still comes from
   * `size`, so this stays a single-axis escape hatch rather than a second
   * geometry.
   */
  strokeWidth?: number;
}

/**
 * Line Simple Spinner - Simple rotating arc
 */
const LineSimpleSpinner: React.FC<{
  dimension: number;
  dimensionVar: string;
  strokeWidth: number;
  strokeWidthVar: string;
  colorClass?: string;
  trackColorClass: string;
  duration: number;
}> = ({
  dimension,
  dimensionVar,
  strokeWidth,
  strokeWidthVar,
  colorClass,
  trackColorClass,
  duration
}) => {
  const radius = (dimension - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = dimension / 2;

  return (
    <svg
      viewBox={`0 0 ${dimension} ${dimension}`}
      className={cn('tw:animate-spin', colorClass)}
      style={{
        width: dimensionVar,
        height: dimensionVar,
        animationDuration: `${duration}s`
      }}
    >
      {/* Track circle */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        className={trackColorClass}
        stroke="currentColor"
        style={{ strokeWidth: strokeWidthVar }}
      />
      {/* Progress arc - uses currentColor to inherit from colorClass */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeDasharray={`${circumference * 0.25} ${circumference * 0.75}`}
        transform={`rotate(-90 ${center} ${center})`}
        style={{ strokeWidth: strokeWidthVar }}
      />
    </svg>
  );
};

/**
 * Line Spinner
 */
const LineSpinner: React.FC<{
  dimension: number;
  dimensionVar: string;
  strokeWidth: number;
  strokeWidthVar: string;
  colorClass?: string;
  duration: number;
}> = ({
  dimension,
  dimensionVar,
  strokeWidth,
  strokeWidthVar,
  colorClass,
  duration
}) => {
  const radius = (dimension - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = dimension / 2;

  return (
    <svg
      viewBox={`0 0 ${dimension} ${dimension}`}
      className={cn('tw:animate-spin', colorClass)}
      style={{
        width: dimensionVar,
        height: dimensionVar,
        animationDuration: `${duration}s`
      }}
    >
      {/* Progress arc - uses currentColor to inherit from colorClass */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeDasharray={`${circumference * 0.3} ${circumference * 0.7}`}
        transform={`rotate(-90 ${center} ${center})`}
        style={{ strokeWidth: strokeWidthVar }}
      />
    </svg>
  );
};

/**
 * Dot Circle Spinner - Dotted circle with angular gradient mask for fade effect
 */
const DotCircleSpinner: React.FC<{
  dimension: number;
  dimensionVar: string;
  strokeWidth: number;
  strokeWidthVar: string;
  colorClass?: string;
  duration: number;
}> = ({
  dimension,
  dimensionVar,
  strokeWidth,
  strokeWidthVar,
  colorClass,
  duration
}) => {
  const radius = (dimension - strokeWidth) / 2;
  const center = dimension / 2;
  // Dash pattern: tiny dash followed by gap
  const dashLength = 0.1;
  const gapLength = 8;

  return (
    <div
      className={cn('tw:relative tw:animate-spin', colorClass)}
      style={{
        width: dimensionVar,
        height: dimensionVar,
        animationDuration: `${duration}s`
      }}
    >
      {/* Dotted circle with conic gradient mask for fade effect */}
      <svg
        viewBox={`0 0 ${dimension} ${dimension}`}
        className="tw:w-full tw:h-full"
        style={{
          WebkitMaskImage:
            'conic-gradient(from 259deg at 50% 50%, #000 0deg, rgba(0, 0, 0, 0.00) 331.875deg, rgba(0, 0, 0, 0.00) 360deg)',
          maskImage:
            'conic-gradient(from 259deg at 50% 50%, #000 0deg, rgba(0, 0, 0, 0.00) 331.875deg, rgba(0, 0, 0, 0.00) 360deg)'
        }}
      >
        {/* Dots - uses currentColor to inherit from colorClass */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeDasharray={`${dashLength} ${gapLength}`}
          style={{ strokeWidth: strokeWidthVar }}
        />
      </svg>
    </div>
  );
};

const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  (
    {
      className,
      variant = 'line-simple',
      size = 'md',
      text = 'Loading...',
      showText = true,
      colorClass,
      duration,
      icon,
      strokeWidth,
      trackColorClass = 'tw:stroke-tertiary',
      ...props
    },
    ref
  ) => {
    const base = sizeConfig[size];
    const config =
      strokeWidth === undefined
        ? base
        : {
            ...base,
            strokeWidth,
            strokeWidthVar: `${strokeWidth}px`
          };
    const effectiveColorClass = colorClass ?? 'tw:text-icon-brand';
    // Default duration: 1s for line variants, 1.2s for dot-circle
    const animationDuration = duration ?? (variant === 'dot-circle' ? 1.2 : 1);

    const renderSpinner = () => {
      switch (variant) {
        case 'line-spinner':
          return (
            <LineSpinner
              dimension={config.dimension}
              dimensionVar={config.dimensionVar}
              strokeWidth={config.strokeWidth}
              strokeWidthVar={config.strokeWidthVar}
              colorClass={effectiveColorClass}
              duration={animationDuration}
            />
          );
        case 'dot-circle':
          return (
            <DotCircleSpinner
              dimension={config.dimension}
              dimensionVar={config.dimensionVar}
              strokeWidth={config.strokeWidth}
              strokeWidthVar={config.strokeWidthVar}
              colorClass={effectiveColorClass}
              duration={animationDuration}
            />
          );
        case 'line-simple':
        default:
          return (
            <LineSimpleSpinner
              dimension={config.dimension}
              dimensionVar={config.dimensionVar}
              strokeWidth={config.strokeWidth}
              strokeWidthVar={config.strokeWidthVar}
              colorClass={effectiveColorClass}
              trackColorClass={trackColorClass}
              duration={animationDuration}
            />
          );
      }
    };

    return (
      <div
        ref={ref}
        className={cn(
          spinnerContainerVariants({ size }),
          'tw:text-text-primary',
          className
        )}
        role="status"
        aria-label={text}
        {...props}
      >
        {icon ? (
          <span className="tw:relative tw:inline-flex tw:items-center tw:justify-center">
            {renderSpinner()}
            <span className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center">
              <Icon
                icon={icon}
                sizeClass={config.iconClass}
                colorClass="tw:text-icon-secondary"
              />
            </span>
          </span>
        ) : (
          renderSpinner()
        )}
        {showText && (
          <Typography variant={config.typography} align="center">
            {text}
          </Typography>
        )}
      </div>
    );
  }
);

Spinner.displayName = 'Spinner';

export { Spinner, spinnerContainerVariants };
